import type {ErrorLogRef, Instant, UUID} from './common'
import type {EntityLink} from './entity-link'
import type {OwnerResponse} from './owner'
import {BadgeVariant} from '@/components/ui/badge'
import {EnumValue} from '@/components/shared/EnumValue'
import {CheckCircle, Clock, Loader2, Play, XCircle, type LucideIcon} from 'lucide-react'
/* eslint-disable i18next/no-literal-string */

// ========== Enums ==========

export const CertificationCampaignStatus = {
    STAGING: 'STAGING',
    BEFORE_GENERATION: 'BEFORE_GENERATION',
    IN_GENERATION: 'IN_GENERATION',
    STARTED: 'STARTED',
    COMPLETED: 'COMPLETED',
    CANCELLED: 'CANCELLED',
} as const
export type CertificationCampaignStatus =
    (typeof CertificationCampaignStatus)[keyof typeof CertificationCampaignStatus]

/**
 * Rich enum carrying UI rendering metadata (badge variant + icon) for
 * {@link CertificationCampaignStatus}. Labels come from app config, so no
 * label lambda.
 */
export class CertificationCampaignStatusBadge extends EnumValue {
    constructor(
        code: string,
        variant: BadgeVariant,
        public readonly icon: LucideIcon,
    ) {
        super(code, variant)
    }

    static readonly STAGING = new CertificationCampaignStatusBadge(
        CertificationCampaignStatus.STAGING, BadgeVariant.OUTLINE, Clock,
    )
    static readonly BEFORE_GENERATION = new CertificationCampaignStatusBadge(
        CertificationCampaignStatus.BEFORE_GENERATION, BadgeVariant.SECONDARY, Loader2,
    )
    static readonly IN_GENERATION = new CertificationCampaignStatusBadge(
        CertificationCampaignStatus.IN_GENERATION, BadgeVariant.SECONDARY, Loader2,
    )
    static readonly STARTED = new CertificationCampaignStatusBadge(
        CertificationCampaignStatus.STARTED, BadgeVariant.SUCCESS, Play,
    )
    static readonly COMPLETED = new CertificationCampaignStatusBadge(
        CertificationCampaignStatus.COMPLETED, BadgeVariant.SUCCESS, CheckCircle,
    )
    static readonly CANCELLED = new CertificationCampaignStatusBadge(
        CertificationCampaignStatus.CANCELLED, BadgeVariant.DESTRUCTIVE, XCircle,
    )

    static readonly values: ReadonlyArray<CertificationCampaignStatusBadge> = [
        CertificationCampaignStatusBadge.STAGING,
        CertificationCampaignStatusBadge.BEFORE_GENERATION,
        CertificationCampaignStatusBadge.IN_GENERATION,
        CertificationCampaignStatusBadge.STARTED,
        CertificationCampaignStatusBadge.COMPLETED,
        CertificationCampaignStatusBadge.CANCELLED,
    ]

    static of(code: CertificationCampaignStatus | null | undefined): CertificationCampaignStatusBadge | undefined {
        return CertificationCampaignStatusBadge.values.find((v) => v.code === code)
    }
}

export const ReviewItemStatus = {
    PENDING: 'PENDING',
    IN_REVIEW: 'IN_REVIEW',
    COMPLETED: 'COMPLETED',
} as const
export type ReviewItemStatus = (typeof ReviewItemStatus)[keyof typeof ReviewItemStatus]

/**
 * Rich enum carrying UI rendering metadata (badge variant + icon) for
 * {@link ReviewItemStatus}.
 */
export class ReviewItemStatusBadge extends EnumValue {
    constructor(
        code: string,
        variant: BadgeVariant,
        public readonly icon: LucideIcon,
    ) {
        super(code, variant)
    }

    static readonly PENDING = new ReviewItemStatusBadge(
        ReviewItemStatus.PENDING, BadgeVariant.OUTLINE, Clock,
    )
    static readonly IN_REVIEW = new ReviewItemStatusBadge(
        ReviewItemStatus.IN_REVIEW, BadgeVariant.DEFAULT, Play,
    )
    static readonly COMPLETED = new ReviewItemStatusBadge(
        ReviewItemStatus.COMPLETED, BadgeVariant.SUCCESS, CheckCircle,
    )

    static readonly values: ReadonlyArray<ReviewItemStatusBadge> = [
        ReviewItemStatusBadge.PENDING,
        ReviewItemStatusBadge.IN_REVIEW,
        ReviewItemStatusBadge.COMPLETED,
    ]

    static of(code: ReviewItemStatus | null | undefined): ReviewItemStatusBadge | undefined {
        return ReviewItemStatusBadge.values.find((v) => v.code === code)
    }
}

export const CertificationDecisionType = {
    UNDECIDED: 'UNDECIDED',
    KEEP: 'KEEP',
    REVOKE: 'REVOKE',
    AUTO_REVOKE: 'AUTO_REVOKE',
} as const
export type CertificationDecisionType =
    (typeof CertificationDecisionType)[keyof typeof CertificationDecisionType]

export const ReviewerType = {
    MANAGER: 'MANAGER',
    PROFILE_OWNER: 'PROFILE_OWNER',
    RESOURCE_OWNER: 'RESOURCE_OWNER',
    CAMPAIGN_OWNER: 'CAMPAIGN_OWNER',
} as const
export type ReviewerType = (typeof ReviewerType)[keyof typeof ReviewerType]

export const NonResponseAction = {
    NO_CHANGE: 'NO_CHANGE',
    AUTO_REVOKE: 'AUTO_REVOKE',
    ESCALATE: 'ESCALATE',
} as const
export type NonResponseAction = (typeof NonResponseAction)[keyof typeof NonResponseAction]

export const CertificationConditionField = {
    RISK_LEVEL: 'RISK_LEVEL',
    SOURCE_TYPE: 'SOURCE_TYPE',
    RESOURCE_TYPE: 'RESOURCE_TYPE',
    ENVIRONMENT: 'ENVIRONMENT',
} as const
export type CertificationConditionField =
    (typeof CertificationConditionField)[keyof typeof CertificationConditionField]

export const CampaignOrigin = {
    MANUAL: 'MANUAL',
    POLICY_GENERATED: 'POLICY_GENERATED',
} as const
export type CampaignOrigin = (typeof CampaignOrigin)[keyof typeof CampaignOrigin]

// ========== Certification Policy ==========

export interface CertificationPolicy {
    id: UUID
    name: string
    description: string | null
    active: boolean
    isDefault: boolean
    priority: number
    validFrom: Instant | null
    validTo: Instant | null
    conditions: CertificationPolicyCondition[]
    conditionExpr: string | null
    parameter: CertificationPolicyParameter | null
    owners: OwnerResponse[]
    version: number
    createdAt: Instant
    updatedAt: Instant
}

export interface CertificationPolicyCondition {
    field: CertificationConditionField
    operator: string
    value: string
}

export interface CertificationPolicyParameter {
    reviewFrequencyDays: number | null
    reviewerType: ReviewerType
    gracePeriodDays: number
    nonResponseAction: NonResponseAction
    autoRevokeOnOverdue: boolean
    owner: EntityLink | null
}

// ========== Certification Campaign ==========

export interface CampaignPolicySummary {
    id: UUID
    displayName: string
}

export interface CertificationCampaign {
    id: UUID
    name: string
    description: string | null
    status: CertificationCampaignStatus
    origin: CampaignOrigin
    scopeDescription: string | null
    startedAt: Instant | null
    dueDate: string
    completedAt: Instant | null
    owners: OwnerResponse[]
    policy: CampaignPolicySummary | null
    version: number
    createdAt: Instant
    updatedAt: Instant
    errorLogRef: ErrorLogRef | null
}

export interface CampaignStatistics {
    totalReviewItems: number
    completedReviewItems: number
    totalAssignments: number
    undecidedCount: number
    keepCount: number
    revokedCount: number
    autoRevokeCount: number
}

export interface PreviewStatistics {
    identityCount: number
    assignmentCount: number
}

// ========== Certification Expression Preview ==========

export interface TestExpressionRequest {
    expression: string
}

export interface TestExpressionItem {
    identityId: UUID
    identityDisplayName: string
    profileId: UUID
    profileCode: string
    profileName: string
    resourceDisplayName: string
    targetSystemName: string
    riskLevel: string
}

export interface TestExpression {
    totalEvaluated: number
    matched: number
    items: TestExpressionItem[]
    tooLarge?: boolean
}

// ========== Unified Scope Preview (preview-scope, spec §6) ==========

/**
 * Request for `POST /api/i/certification-policies/preview-scope`. Exactly one of
 * `conditionExpr` (CEL) XOR `conditions[]` (structured) is supplied; both empty
 * or absent previews the full active-assignment population ("certify everything").
 * Supplying both is a 400. A `SOURCE_TYPE` condition is a 400 (unsupported in the
 * SQL conditions path — `CE0015_SOURCE_TYPE_NOT_SUPPORTED`).
 */
export interface PreviewScopeRequest {
    conditionExpr?: string
    conditions?: CertificationPolicyConditionRequest[]
}

export type PreviewScopeItem = TestExpressionItem

/**
 * Response for `preview-scope`. In the normal case `totalEvaluated` / `matched` /
 * `items` are present and `tooLarge` is `false`. In the expression-mode
 * population-guard case the backend returns `{ tooLarge: true }` with
 * `totalEvaluated` / `matched` / `items` ABSENT — the panel treats their absence
 * as the `tooLarge` state (spec §4.2, §6, §7.3).
 */
export interface PreviewScopeResponse {
    totalEvaluated?: number
    matched?: number
    items?: PreviewScopeItem[]
    tooLarge?: boolean
}

// ========== Certification Review Item ==========

export interface CertificationReviewItem {
    id: UUID
    campaignId: UUID
    campaignName: string
    campaignDueDate: string
    identity: EntityLink
    reviewer: EntityLink | null
    originalReviewer: EntityLink | null
    status: ReviewItemStatus
    totalAssignments: number
    decidedAssignments: number
    selfCertification: boolean
    certifiedAt: Instant | null
    certifiedBy: string | null
    createdAt: Instant
}

export type CertificationReviewItemPage = import('./page').Page<CertificationReviewItem>

export interface ReviewerCampaignSummary {
    id: UUID
    name: string
    dueDate: string
}

// ========== Certification Decision ==========

export interface CertificationDecision {
    id: UUID
    reviewItemId: UUID
    assignmentId: UUID | null
    accessProfile: EntityLink | null
    decisionType: CertificationDecisionType | null
    autoRevokeReason: string | null
    riskLevel: string | null
    comment: string | null
    decidedBy: string | null
    decidedAt: Instant | null
    decisionDurationMs: number | null
}

// ========== Dashboard ==========

export interface CertificationDashboardSummary {
    activeCampaigns: number
    overdueCampaigns: number
    totalReviewItems: number
    completedReviewItems: number
    totalAssignments: number
    undecidedCount: number
    keepCount: number
    revokedCount: number
    completionPercent: number
}

export interface ActiveCampaignDetail {
    id: UUID
    name: string
    dueDate: string
    overdue: boolean
    totalReviewItems: number
    completedReviewItems: number
    undecidedCount: number
    keepCount: number
    revokedCount: number
    completionPercent: number
}

export type CertificationPolicyPage = import('./page').Page<CertificationPolicy>

/**
 * Aggregate counts for a certification policy's campaigns. Mirrors the backend
 * {@code CampaignSummaryByPolicyResponse} record — used by the policy detail
 * page to render its campaign-count badge and gate the "Create Campaign"
 * button without listing campaigns just to extract page metadata.
 */
export interface CampaignSummaryByPolicy {
    totalCampaigns: number
    hasOpenCampaign: boolean
}

// ========== Requests ==========

export interface CreateCampaignRequest {
    name: string
    description: string | null
    dueDate: string
    ownerPartyId: UUID
    certificationPolicyId?: UUID
}

export interface UpdateCampaignRequest {
    name: string
    description: string | null
    dueDate: string
    version: number
}

export interface DecideRequest {
    assignmentId: UUID
    decisionType: CertificationDecisionType
    comment: string | null
}

export interface BulkDecideRequest {
    decisionType: CertificationDecisionType
    comment: string | null
    riskLevel: string | null
    currentDecisionType: CertificationDecisionType | null
}

export interface BulkDecide {
    affected: number
    decisionType: string
}

// ========== Navigation ==========

export interface ReviewItemNavigation {
    previous: ReviewItemRef | null
    next: ReviewItemRef | null
}

export interface ReviewItemRef {
    id: UUID
    displayName: string
}

export type CertificationDecisionPage = import('./page').Page<CertificationDecision>

export interface ReassignRequest {
    newReviewerIdentityId: UUID
}

export interface BulkCertifyRequest {
    reviewItemIds: UUID[]
}

export interface GenerateCampaigns {
    campaigns: CertificationCampaign[]
    skippedPolicies: string[]
}

export interface CertificationPolicyConditionRequest {
    field: string
    operator: string
    value: string
}

export interface CreateCertificationPolicyParameterRequest {
    reviewFrequencyDays?: number
    reviewerType: string
    gracePeriodDays?: number
    nonResponseAction?: string
    autoRevokeOnOverdue?: boolean
    ownerPartyId?: UUID
}

export interface CreateCertificationPolicyRequest {
    name: string
    description?: string
    priority: number
    active: boolean
    validFrom?: Instant
    validTo?: Instant
    conditions?: CertificationPolicyConditionRequest[]
    conditionExpr?: string
    parameter: CreateCertificationPolicyParameterRequest
}

export interface UpdateCertificationPolicyParameterRequest {
    reviewFrequencyDays?: number
    reviewerType: string
    gracePeriodDays?: number
    nonResponseAction?: string
    autoRevokeOnOverdue?: boolean
    ownerPartyId?: UUID
}

export interface UpdateCertificationPolicyRequest {
    name: string
    description?: string
    priority: number
    active: boolean
    validFrom?: Instant
    validTo?: Instant
    conditions?: CertificationPolicyConditionRequest[]
    conditionExpr?: string
    parameter: UpdateCertificationPolicyParameterRequest
    version?: number
}

/* eslint-disable i18next/no-literal-string -- field name constants for backend API */
export const BulkCertifyRequestFields = {
    reviewItemIds: 'reviewItemIds',
} as const

export const BulkDecideRequestFields = {
    decisionType: 'decisionType',
    comment: 'comment',
    riskLevel: 'riskLevel',
    currentDecisionType: 'currentDecisionType',
} as const

export const BulkDecideResponseFields = {
    affected: 'affected',
    decisionType: 'decisionType',
} as const

export const CertificationCampaignResponseFields = {
    id: 'id',
    name: 'name',
    description: 'description',
    status: 'status',
    origin: 'origin',
    scopeDescription: 'scopeDescription',
    startedAt: 'startedAt',
    dueDate: 'dueDate',
    completedAt: 'completedAt',
    owners: 'owners',
    policy: 'policy',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
    errorLogRef: 'errorLogRef',
} as const

export const CertificationDecisionResponseFields = {
    id: 'id',
    reviewItemId: 'reviewItemId',
    assignmentId: 'assignmentId',
    accessProfile: 'accessProfile',
    decisionType: 'decisionType',
    autoRevokeReason: 'autoRevokeReason',
    riskLevel: 'riskLevel',
    comment: 'comment',
    decidedBy: 'decidedBy',
    decidedAt: 'decidedAt',
    decisionDurationMs: 'decisionDurationMs',
} as const

export const CertificationPolicyResponseFields = {
    id: 'id',
    name: 'name',
    description: 'description',
    priority: 'priority',
    active: 'active',
    validFrom: 'validFrom',
    validTo: 'validTo',
    isDefault: 'isDefault',
    conditions: 'conditions',
    conditionExpr: 'conditionExpr',
    parameter: 'parameter',
    owners: 'owners',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
} as const

export const CertificationReviewItemResponseFields = {
    id: 'id',
    campaignId: 'campaignId',
    campaignName: 'campaignName',
    campaignDueDate: 'campaignDueDate',
    identity: 'identity',
    reviewer: 'reviewer',
    originalReviewer: 'originalReviewer',
    status: 'status',
    totalAssignments: 'totalAssignments',
    decidedAssignments: 'decidedAssignments',
    selfCertification: 'selfCertification',
    certifiedAt: 'certifiedAt',
    certifiedBy: 'certifiedBy',
    createdAt: 'createdAt',
} as const

export const CreateCampaignRequestFields = {
    name: 'name',
    description: 'description',
    dueDate: 'dueDate',
    ownerPartyId: 'ownerPartyId',
    certificationPolicyId: 'certificationPolicyId',
} as const

export const CreateCertificationPolicyRequestFields = {
    name: 'name',
    description: 'description',
    priority: 'priority',
    active: 'active',
    validFrom: 'validFrom',
    validTo: 'validTo',
    conditions: 'conditions',
    conditionExpr: 'conditionExpr',
    parameter: 'parameter',
} as const

export const DecideRequestFields = {
    assignmentId: 'assignmentId',
    decisionType: 'decisionType',
    comment: 'comment',
} as const

export const GenerateCampaignsResponseFields = {
    campaigns: 'campaigns',
    skippedPolicies: 'skippedPolicies',
} as const

export const ReassignRequestFields = {
    newReviewerIdentityId: 'newReviewerIdentityId',
} as const

export const ReviewItemNavigationResponseFields = {
    previous: 'previous',
    next: 'next',
} as const

export const UpdateCampaignRequestFields = {
    name: 'name',
    description: 'description',
    dueDate: 'dueDate',
    version: 'version',
} as const

export const UpdateCertificationPolicyRequestFields = {
    name: 'name',
    description: 'description',
    priority: 'priority',
    active: 'active',
    validFrom: 'validFrom',
    validTo: 'validTo',
    conditions: 'conditions',
    conditionExpr: 'conditionExpr',
    parameter: 'parameter',
    version: 'version',
} as const

export const CertificationCampaignFilterParams = {
    status: 'status',
} as const

export const CertificationPolicyQueryParams = {
    policy: 'policy',
    name: 'name',
    description: 'description',
    priority: 'priority',
    active: 'active',
    reviewFrequencyDays: 'reviewFrequencyDays',
    ownerPartyId: 'ownerPartyId',
} as const
/* eslint-enable i18next/no-literal-string */
